import { Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { DateRangePicker } from '@/components/ui/date-range-picker'
import type { DateRangeValue } from '@/components/ui/date-range-picker'
import { Select } from '@/components/ui/select'
import { FilterFormWrapper } from '@/components/FilterFormWrapper'
import { fetchUnits } from '@/api/units.api'

export interface AccountFilterValues {
  fullName: string
  email: string
  unitId: string | undefined
  unitName?: string
  status: number | undefined
  time: DateRangeValue | undefined
}

interface AccountFilterPanelProps {
  values: AccountFilterValues
  unitOptions?: { value: string; label: string }[]
  onChange: (values: AccountFilterValues) => void
  onSearch: () => void
  showUnitColumn: boolean
}

export function AccountFilterPanel({
  values,
  onChange,
  onSearch,
  showUnitColumn,
}: AccountFilterPanelProps) {
  function set<TKey extends keyof AccountFilterValues>(
    key: TKey,
    value: AccountFilterValues[TKey],
  ) {
    onChange({ ...values, [key]: value })
  }

  return (
    <FilterFormWrapper
      onSearch={onSearch}
      className="rounded-xl border border-gray-300 bg-white px-6 py-5"
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex min-w-[200px] flex-1 flex-col gap-1">
            <span className="text-sm font-medium">Tìm kiếm họ và tên</span>
            <Input
              placeholder="Nhập từ khóa"
              value={values.fullName}
              onChange={(e: { target: { value: string } }) =>
                set('fullName', e.target.value)
              }
            />
          </div>
          <div className="flex min-w-[200px] flex-1 flex-col gap-1">
            <span className="text-sm font-medium">Tìm kiếm email</span>
            <Input
              placeholder="Nhập từ khóa"
              value={values.email}
              onChange={(e: { target: { value: string } }) =>
                set('email', e.target.value)
              }
            />
          </div>
          {showUnitColumn && (
            <div className="flex min-w-[200px] flex-1 flex-col gap-1">
              <span className="text-sm font-medium">Đơn vị</span>
              <Select
                initialPage={0}
                cacheKey="account-filter-unit-select"
                lazy
                placeholder="Tất cả đơn vị"
                allowClear
                value={values.unitId ?? undefined}
                className="w-full"
                enableRemoteSearch
                allLabel="Tất cả đơn vị"
                initialOptions={
                  values.unitId && values.unitName
                    ? [{ value: values.unitId, label: values.unitName }]
                    : []
                }
                loadOptions={async ({ page, signal, searchText }) => {
                  const res = await fetchUnits(
                    page,
                    10,
                    { keySearch: searchText?.trim() || undefined },
                    signal,
                  )
                  const arr = Array.isArray(res.items) ? res.items : []
                  const pageSize = 10
                  const hasNextPage = arr.length >= pageSize

                  return {
                    options: arr.map((u: any) => ({
                      value: u.id,
                      label: u.name,
                    })),
                    hasNextPage,
                    nextPage: hasNextPage ? page + 1 : undefined,
                  }
                }}
                onLazyValueChange={(unitId, option) => {
                  onChange({
                    ...values,
                    unitId: unitId,
                    unitName: option?.label ? String(option.label) : undefined,
                  })
                }}
              />
            </div>
          )}
          <div className="flex min-w-[200px] flex-1 flex-col gap-1">
            <span className="text-sm font-medium">Trạng thái</span>
            <Select
              placeholder="Tất cả"
              value={values.status}
              onValueChange={(v) => set('status', v)}
              allowClear
              className="w-full"
              options={[
                { value: 1, label: 'Hoạt động' },
                { value: 0, label: 'Không hoạt động' },
              ]}
            />
          </div>
          <div className="flex flex-1 min-w-[300px] flex-col gap-1">
            <span className="text-sm font-medium">Thời gian</span>
            <DateRangePicker
              value={values.time}
              onChange={(v) => set('time', v)}
              className="w-full rounded-full"
            />
          </div>
          <Button
            variant="outline"
            size="md"
            onClick={onSearch}
            className="gap-2 rounded-full border-[#CE7A58] text-[#CE7A58] hover:bg-[#CE7A58]/10"
          >
            <Search size={16} />
            Tìm kiếm
          </Button>
        </div>
      </div>
    </FilterFormWrapper>
  )
}
